import Hue from '@assets/hue.svg';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import { easeInOut } from '@config/animations.config';
import { IProductComparisonCard } from '@models/IProductComparisonCard';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props extends IProductComparisonCard {
  readonly open: boolean;
  readonly dataTestId: string;
}

const ProductComparisonCard = ({
  buyNowLink,
  dataTestId = 'Product-Comparison-Card',
  fromPriceLabel,
  highlightIcon,
  highlights,
  icon,
  learnMoreLink,
  open,
  pricingData,
  shippingLabel,
  subTitle,
  thumbnail,
  title,
  trackingData,
  vatLabel,
}: Props) => {
  const [isHovered, setHover] = useState(false);
  const [ctaBoxHeight, setCtaBoxHeight] = useState(0);

  const isXLarge = useMediaQuery(1440);
  const trackingDataJson = JSON.stringify(trackingData ?? '');

  const ctaBox = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCtaBoxHeight(ctaBox.current?.clientHeight || 0);
  }, []);

  const filteredHighlights = open ? highlights : highlights?.slice(0, 5);

  const contVariants = {
    hover: { y: -ctaBoxHeight },
    initial: { y: 0 },
  };

  const ctaVariants = {
    hover: { opacity: 1 },
    initial: { opacity: isXLarge ? 1 : 0 },
  };

  const imgVariants = {
    hover: { y: -15, scale: 1.1 },
    initial: { y: 0, scale: 1 },
  };

  const hueVariants = {
    hover: { opacity: 0.2 },
    initial: { opacity: 0 },
  };

  const handleIntentToBuyEvent = (contentType: string) => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'intentToBuy',
      content_type: contentType,
      items: [mapToNewTrackingData(trackingData)],
    });
  };

  const handleSelectItemEvent = () => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'selectItem',
      ecommerce: {
        currency: trackingData.currency,
        items: [mapToNewTrackingData(trackingData)],
      },
    });
  };

  return (
    <div
      aria-label={title}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      // eslint-disable-next-line react/no-unknown-property
      track-product-impression="true"
      data-product={trackingDataJson}
      className="pointer-events-auto flex h-full w-full flex-col justify-end gap-5 overflow-hidden whitespace-normal rounded-[10px] bg-grey-600 px-7 py-10 text-white xl:py-15"
      data-testid={dataTestId}
    >
      <div className="flex h-full w-full flex-col gap-7.5">
        <motion.div
          initial="initial"
          className="relative flex h-[200px] w-full shrink-0 items-center justify-center pb-10 md:h-[250px] xl:h-[320px]"
          animate={isHovered && !isXLarge ? 'hover' : 'initial'}
          variants={imgVariants}
          transition={easeInOut}
        >
          {Hue && thumbnail && (
            <motion.div
              initial="initial"
              animate={isHovered && !isXLarge ? 'hover' : 'initial'}
              variants={hueVariants}
              className="absolute left-0 top-0 flex h-full w-full items-center justify-center pb-10"
            >
              <img
                src={`${Hue}`}
                alt="Hue SVG"
                className="h-[500px] w-full"
                data-testid={`${dataTestId}-Hue`}
              />
            </motion.div>
          )}
          {learnMoreLink?.url || buyNowLink?.url ? (
            <a
              href={learnMoreLink?.url || buyNowLink?.url}
              onClick={() => {
                if (learnMoreLink?.url)
                  handleIntentToBuyEvent('product picture');
                else if (buyNowLink?.url) handleSelectItemEvent();
              }}
              aria-label={title}
              className="flex h-full w-full"
              data-testid={`${dataTestId}-Link`}
            >
              <Image
                image={thumbnail}
                classes="w-full h-full object-contain relative"
                imageClasses="h-full w-full object-contain"
                sizes="(max-width: 768px) 376px, 500px"
                dataTestId={`${dataTestId}-Link-Image`}
              />
            </a>
          ) : (
            <Image
              image={thumbnail}
              classes="w-full h-full object-contain relative"
              imageClasses="h-full w-full object-contain"
              sizes="(max-width: 768px) 376px, 500px"
              dataTestId={`${dataTestId}-Image`}
            />
          )}
        </motion.div>

        <motion.div
          initial="initial"
          animate={isHovered && !isXLarge ? 'hover' : 'initial'}
          variants={contVariants}
          transition={easeInOut}
          className="relative flex w-full grow flex-col items-start justify-start gap-7.5"
        >
          {icon && (
            <Image
              image={icon}
              imageClasses="w-[50px] h-[25px] md:w-[70px] md:h-[33px] object-contain"
              sizes="300px"
              dataTestId={`${dataTestId}-Icon`}
            />
          )}

          {!icon && title && (
            <div className="flex h-[25px] items-end md:h-[33px]">
              <p className="h5" data-testid={`${dataTestId}-Title`}>
                {title}
              </p>
            </div>
          )}

          {subTitle && (
            <p className="body-three" data-testid={`${dataTestId}-Subtitle`}>
              {subTitle}
            </p>
          )}

          <hr className="w-full border-t border-grey-450" />

          <div className="flex w-full flex-col gap-[15px] md:gap-7.5">
            {highlightIcon && (
              <Image
                image={highlightIcon}
                imageClasses="w-full h-full h-10 w-10 sm:h-11 sm:w-11 xl:h-13 xl:w-13"
                sizes="300px"
                dataTestId={`${dataTestId}-Highlight-Icon`}
              />
            )}

            <ul className="w-full">
              {filteredHighlights?.map((highlight, index) => (
                <motion.li
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="body-two mb-8 last-of-type:mb-0"
                  key={highlight}
                  data-testid={`${dataTestId}-Highlight-${index}`}
                >
                  {highlight}
                </motion.li>
              ))}
            </ul>
          </div>

          <hr className="mt-auto w-full border-t border-grey-450" />

          <div>
            {pricingData?.formattedCalculatedPrice && (
              <PriceDisplayText
                pricingData={pricingData}
                vatLabel={vatLabel}
                shippingLabel={shippingLabel}
                fromPriceLabel={fromPriceLabel}
                priceClassName="text-grey-500 dark:text-yellow"
                dataTestId={`${dataTestId}-Price-Display-Text`}
              />
            )}

            <motion.div
              initial="initial"
              animate={isHovered && !isXLarge ? 'hover' : 'initial'}
              variants={ctaVariants}
              transition={{
                duration: 0.3,
              }}
              ref={ctaBox}
              className="flex flex-col flex-wrap items-start justify-start gap-5 pt-5 xl:absolute xl:top-full xl:flex-row xl:gap-[10px]"
            >
              {learnMoreLink?.url && learnMoreLink?.name && (
                <Button
                  link={learnMoreLink}
                  secondary
                  linkCallback={() =>
                    handleIntentToBuyEvent(learnMoreLink.name || 'Learn more')
                  }
                  dataTestId={`${dataTestId}-Learn-More-Link`}
                />
              )}
              {buyNowLink?.url && buyNowLink?.name && (
                <Button
                  link={buyNowLink}
                  linkCallback={handleSelectItemEvent}
                  dataTestId={`${dataTestId}-Buy-Now-Link`}
                />
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProductComparisonCard;
